// src/utils/countryFlags.ts

/**
 * Currency code to country code mapping
 * Maps ISO 4217 currency codes to ISO 3166-1 alpha-2 country codes
 */
export const CURRENCY_TO_COUNTRY: Record<string, string> = {
  // Major currencies
  USD: "US",
  EUR: "EU",
  GBP: "GB",
  JPY: "JP",
  CNY: "CN",
  CHF: "CH",
  CAD: "CA",
  AUD: "AU",
  NZD: "NZ",
  // Americas
  MXN: "MX",
  BRL: "BR",
  ARS: "AR",
  COP: "CO",
  PEN: "PE",
  CLP: "CL",
  CUC: "CU",
  DOP: "DO",
  GTQ: "GT",
  URY: "UY",
  CRC: "CR",
  PAB: "PA",
  BOB: "BO",
  PYG: "PY",
  SVC: "SV",
  TTD: "TT",
  HNL: "HN",
  JMD: "JM",
  NIO: "NI",
  HTG: "HT",
  BSD: "BS",
  SRD: "SR",
  GYD: "GY",
  VES: "VE",
  KYD: "KY",
  // Europe
  RUB: "RU",
  TRY: "TR",
  SEK: "SE",
  PLN: "PL",
  NOK: "NO",
  DKK: "DK",
  CZK: "CZ",
  HUF: "HU",
  RON: "RO",
  UAH: "UA",
  BYR: "BY",
  RSD: "RS",
  BAM: "BA",
  ISK: "IS",
  ALL: "AL",
  MKD: "MK",
  MDL: "MD",
  BGN: "BG",
  // Asia
  KRW: "KR",
  INR: "IN",
  IDR: "ID",
  SAR: "SA",
  SGD: "SG",
  HKD: "HK",
  TWD: "TW",
  THB: "TH",
  MYR: "MY",
  PHP: "PH",
  VND: "VN",
  PKR: "PK",
  BDT: "BD",
  LKR: "LK",
  MMK: "MM",
  KHR: "KH",
  LAK: "LA",
  MNT: "MN",
  KPW: "KP",
  NPR: "NP",
  BND: "BN",
  MOP: "MO",
  MVR: "MV",
  // Middle East
  ILS: "IL",
  IRR: "IR",
  IQD: "IQ",
  QAR: "QA",
  AED: "AE",
  KWD: "KW",
  BHD: "BH",
  OMR: "OM",
  JOD: "JO",
  LBP: "LB",
  SYP: "SY",
  YER: "YE",
  // Central Asia & Caucasus
  KZT: "KZ",
  AZN: "AZ",
  GEL: "GE",
  TMT: "TM",
  UZS: "UZ",
  TJS: "TJ",
  KGS: "KG",
  AMD: "AM",
  AFN: "AF",
  // Africa
  ZAR: "ZA",
  NGN: "NG",
  EGP: "EG",
  DZD: "DZ",
  MAD: "MA",
  KES: "KE",
  ETB: "ET",
  TZS: "TZ",
  GHS: "GH",
  UGX: "UG",
  ZMW: "ZM",
  AOA: "AO",
  SDG: "SD",
  LYD: "LY",
  CDF: "CD",
  MZN: "MZ",
  BWP: "BW",
  NAD: "NA",
  MUR: "MU",
  MGA: "MG",
  RWF: "RW",
  GNF: "GN",
  MRO: "MR",
  SLL: "SL",
  MWK: "MW",
  SSP: "SS",
  TND: "TN",
  ERN: "ER",
  SZL: "SZ",
  BIF: "BI",
  LSL: "LS",
  LRD: "LR",
  CVE: "CV",
  DJF: "DJ",
  SCR: "SC",
  SOS: "SO",
  GMD: "GM",
  KMF: "KM",
  STD: "ST",
  ZIG: "ZW",
  XAF: "CM", // Central African CFA franc
  XOF: "SN", // West African CFA franc
  // Oceania
  PGK: "PG",
  XPF: "NC", // CFP franc
  FJD: "FJ",
};

/**
 * Gets the country flag emoji for a currency pair (e.g., "USDJPY" -> 🇯🇵)
 * Extracts the non-USD currency from the pair and returns its flag
 * @param currencyPair - Currency pair string (e.g., "USDJPY", "EURUSD", "DXY")
 * @returns Flag emoji for the non-USD currency in the pair
 */
export const getCurrencyFlag = (currencyPair: string): string => {
  if (!currencyPair) return "";

  // Special case for DXY (Dollar Index)
  if (currencyPair === "DXY") {
    return getCountryFlag("US");
  }

  // Extract the non-USD currency code from the pair
  let currencyCode: string;
  if (currencyPair.startsWith("USD")) {
    currencyCode = currencyPair.slice(3, 6);
  } else if (currencyPair.endsWith("USD")) {
    currencyCode = currencyPair.slice(0, 3);
  } else {
    // For non-USD pairs, use the first currency
    currencyCode = currencyPair.slice(0, 3);
  }

  const countryCode = CURRENCY_TO_COUNTRY[currencyCode];
  return countryCode ? getCountryFlag(countryCode) : "";
};

/**
 * Converts a country code to its flag emoji using Unicode regional indicator symbols
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'CN', 'GB')
 * @returns Flag emoji for the country or the country code if not found
 */
export const getCountryFlag = (
  countryCode: string | null | undefined
): string => {
  if (!countryCode || typeof countryCode !== "string") {
    return "";
  }

  // Convert to uppercase and trim
  const code = countryCode.trim().toUpperCase();

  // Handle special cases
  const specialCases: Record<string, string> = {
    UK: "🇬🇧", // UK -> Great Britain
    EU: "🇪🇺", // European Union
  };

  if (specialCases[code]) {
    return specialCases[code];
  }

  // Convert ISO 3166-1 alpha-2 country code to flag emoji
  // Each letter is converted to a regional indicator symbol
  // A = 0x1F1E6, B = 0x1F1E7, ..., Z = 0x1F1FF
  if (code.length === 2 && /^[A-Z]{2}$/.test(code)) {
    const codePoints = [...code].map(
      (char) => 0x1f1e6 + char.charCodeAt(0) - 65
    );
    return String.fromCodePoint(...codePoints);
  }

  // If not a valid country code, return the original code
  return code;
};

/**
 * Gets a country flag with the country code as fallback text
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Flag emoji followed by country code, or just the code if flag not available
 */
export const getCountryFlagWithCode = (
  countryCode: string | null | undefined
): string => {
  if (!countryCode) {
    return "";
  }

  const flag = getCountryFlag(countryCode);
  const code = countryCode.trim().toUpperCase();

  // If flag is same as code (no flag found), just return code
  if (flag === code) {
    return code;
  }

  // Return flag with code
  return `${flag} ${code}`;
};
